import { z, type ZodType } from './schema'
import { Column } from './column'
import { Condition } from './condition'
import { physicalTableName, tableSqlRef } from './naming'
import type {
  Dialect,
  IndexDefinition,
  IndexMeta,
  IndexOptionValue,
  ParamWithMeta,
  PgTriggerDefinition,
  PrimaryKeyDefinition,
  TableConfig,
  QueryContext,
  SqlExpression,
  SqlFragment,
  SqliteTableConfig,
  TableExtension,
  TableMeta,
  TableWithConfig,
  TableWithOptions,
} from './types'

const immediateTransactionSql = Symbol.for('@w/db.immediateTransactionSql')

/**
 * Base type for any Column - uses 'any' to avoid variance issues
 */
// oxlint-disable-next-line typescript/no-explicit-any
export type AnyColumn = Column<any, any, any>

/**
 * Phantom brand type to carry fn type info on unbound tables.
 * Database reads this to infer the fn type for each table.
 */
// oxlint-disable-next-line typescript/no-explicit-any
export interface WithFnBrand<Fns = any> {
  readonly __fnDef?: Fns
}

/**
 * Represents an unbound table that hasn't been assigned a name yet.
 * Tables exist in this state until `createDb()` assigns their name from the object key.
 *
 * Columns are exposed as properties for use in foreign key references,
 * e.g., `references(() => users.id)` works with unbound tables.
 */
export class UnboundTable<Columns extends Record<string, AnyColumn>> {
  readonly __columns: Columns
  private _selectSchemaCache?: ZodType<SelectType<Columns>, SelectType<Columns>>
  readonly __meta__: TableMeta
  // Store builders with any type to avoid contravariance issues with AnyUnboundTable
  // oxlint-disable-next-line typescript/no-explicit-any
  readonly extensionBuilder?: (columns: any) => TableExtension[]
  // oxlint-disable-next-line typescript/no-explicit-any
  readonly indexBuilder?: (columns: any) => IndexDefinition[]
  // oxlint-disable-next-line typescript/no-explicit-any
  readonly triggerBuilder?: (columns: any) => PgTriggerDefinition[]
  // oxlint-disable-next-line typescript/no-explicit-any
  readonly primaryKeyBuilder?: (columns: any) => readonly AnyColumn[]
  // oxlint-disable-next-line typescript/no-explicit-any
  readonly _fnBuilder?: (table: any, db: QueryContext) => any;

  [key: string]: unknown

  // oxlint-disable-next-line typescript/no-explicit-any
  readonly _with?: TableWithConfig<any>
  readonly _schema?: string
  readonly _comment?: string
  readonly _clusteredBy?: string
  readonly _withoutRowId?: boolean

  private static readonly RESERVED_NAMES = new Set(['all'])

  /** Set by createDb() before binding so toString() works in view query closures. */
  _name: string = ''

  constructor(
    columns: Columns,
    readonly dialect: Dialect,
    extensionBuilder?: (columns: Columns) => TableExtension[],
    indexBuilder?: (columns: Columns) => IndexDefinition[],
    primaryKeyBuilder?: (columns: Columns) => readonly AnyColumn[],
    withConfig?: TableWithConfig<Columns>,
    // oxlint-disable-next-line typescript/no-explicit-any
    fnBuilder?: (table: any, db: QueryContext) => any,
    schema?: string,
    comment?: string,
    clusteredBy?: string,
    meta?: TableMeta,
    withoutRowId?: boolean,
    triggerBuilder?: (columns: Columns) => PgTriggerDefinition[],
  ) {
    this.__columns = columns
    this.__meta__ = meta ?? {}
    this.extensionBuilder = extensionBuilder
    this.indexBuilder = indexBuilder
    this.triggerBuilder = triggerBuilder
    this.primaryKeyBuilder = primaryKeyBuilder
    this._with = withConfig
    this._fnBuilder = fnBuilder
    this._schema = schema
    this._comment = comment
    this._clusteredBy = clusteredBy
    this._withoutRowId = withoutRowId
    // Expose columns as properties and set column names early for toString()
    for (const [colName, column] of Object.entries(columns)) {
      column._setColumnName(colName)
      if (!UnboundTable.RESERVED_NAMES.has(colName)) {
        ;(this as Record<string, unknown>)[colName] = column
      }
    }
  }

  /**
   * Returns the quoted table name for use in view query template literals.
   */
  toString(): string {
    if (!this._name)
      throw new Error('Table not yet bound. Use inside createDb().')
    return this.__sqlRef
  }

  /**
   * Table name getter. Enables isTable() detection so `${unboundRef}` interpolates
   * correctly when a table-level fn closure captures a sibling by import.
   */
  get __name(): string {
    if (!this._name)
      throw new Error('Table not yet bound. Use inside createDb().')
    return this._name
  }

  /** Dialect-aware, schema-qualified SQL reference. Mirrors Table.__sqlRef. */
  get __sqlRef(): string {
    return this.__sqlRefFor(this.dialect)
  }

  __sqlRefFor(dialect: Dialect): string {
    return tableSqlRef(this.__name, this._schema, dialect)
  }

  /** SqlFragment impl — lets template literal interpolation use unbound tables. */
  toSQL(
    _paramOffset: number,
    dialect: Dialect = this.dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    return { sql: this.__sqlRefFor(dialect), params: [] }
  }

  get all(): SqlFragment {
    return allColumnsFragment(
      (dialect) => this.__sqlRefFor(dialect ?? this.dialect),
      this.__columns,
    )
  }

  /**
   * The TypeScript type for SELECT results (type-only, available before binding)
   */
  get __selectType(): SelectType<Columns> {
    throw new Error('__selectType is a type-only property')
  }

  /**
   * The TypeScript type for INSERT values (type-only, available before binding)
   */
  get __insertType(): InsertType<Columns> {
    throw new Error('__insertType is a type-only property')
  }

  /**
   * Zod schema for validating SELECT results. Mirrors the Table getter so
   * sibling-capture closures (which hold an unbound ref) can read rows
   * without pulling the bound wrapper through fn context.
   */
  get __selectSchema(): ZodType<SelectType<Columns>, SelectType<Columns>> {
    // Columns are fixed per instance (Column caches its zodType too): build once, not per read.
    return (this._selectSchemaCache ??= (() => {
      const shape: Record<string, ZodType> = {}
      for (const [colName, column] of Object.entries(this.__columns)) {
        shape[colName] = column.zodType
      }
      return z.object(shape) as unknown as ZodType<
        SelectType<Columns>,
        SelectType<Columns>
      >
    })())
  }

  /**
   * Binds the table to a name, returning a fully-formed Table.
   * This is called by createDb() to assign the table name from the object key.
   */
  _bind(tableName: string): Table<Columns> {
    return new Table(
      tableName,
      this.__columns,
      this.dialect,
      this.extensionBuilder,
      this.indexBuilder,
      this.primaryKeyBuilder,
      this._with,
      this._fnBuilder,
      this._schema,
      this._comment,
      this._clusteredBy,
      this.__meta__,
      this._withoutRowId,
      this.triggerBuilder,
    )
  }
}

/**
 * Helper types for extracting TypeScript types from column definitions
 */

// Extract the TypeScript type from a Column
type ColumnType<C> = C extends Column<infer T, infer _I, infer _G> ? T : never

// Extract the insertion type from a Column
type ColumnInsertionType<C> =
  C extends Column<infer _T, infer I, infer _G> ? I : never

// Check if a column should be included in insert type
type IsInsertableColumn<C> =
  ColumnInsertionType<C> extends 'generated' ? false : true

// Get the insert type for a single column
type ColumnInsertType<C> =
  ColumnInsertionType<C> extends 'required'
    ? ColumnType<C>
    : ColumnInsertionType<C> extends 'optional'
      ? ColumnType<C> | null | undefined
      : ColumnInsertionType<C> extends 'withDefault'
        ? ColumnType<C> | undefined
        : never

// Build the select type from columns (all columns, nullable as appropriate)
type SelectType<Columns extends Record<string, AnyColumn>> = {
  [K in keyof Columns]: Columns[K] extends Column<infer T, infer I, infer _G>
    ? I extends 'optional'
      ? T | null
      : T
    : never
}

// Build the insert type from columns (exclude generated, make optionals optional)
type InsertType<Columns extends Record<string, AnyColumn>> = {
  [K in keyof Columns as IsInsertableColumn<Columns[K]> extends true
    ? ColumnInsertionType<Columns[K]> extends 'required'
      ? K
      : never
    : never]: ColumnInsertType<Columns[K]>
} & {
  [K in keyof Columns as IsInsertableColumn<Columns[K]> extends true
    ? ColumnInsertionType<Columns[K]> extends 'required'
      ? never
      : K
    : never]?: ColumnInsertType<Columns[K]>
}

type UpdateType<Columns extends Record<string, AnyColumn>> = {
  [K in keyof InsertType<Columns>]?:
    | InsertType<Columns>[K]
    | (K extends keyof Columns ? SqlExpression<ColumnType<Columns[K]>> : never)
}

/**
 * Index builder functions
 */
export function index(...columns: AnyColumn[]): IndexDefinition {
  return {
    columns: columns.map((c) => c.name),
    unique: false,
    terms: columns.map((c) => ({ type: 'column', name: c.name })),
  }
}

export function uniqueIndex(...columns: AnyColumn[]): IndexDefinition {
  return {
    columns: columns.map((c) => c.name),
    unique: true,
    terms: columns.map((c) => ({ type: 'column', name: c.name })),
  }
}

type CustomIndexTerm = AnyColumn | SqlFragment

type CustomIndexConfig =
  | {
      name?: string
      on: readonly AnyColumn[]
      using?: string
      with?: SqlFragment
      where?: SqlFragment
      unique?: boolean
      meta?: IndexMeta
    }
  | {
      name: string
      on: readonly CustomIndexTerm[]
      using?: string
      with?: SqlFragment
      where?: SqlFragment
      unique?: boolean
      meta?: IndexMeta
    }

type LegacyCustomIndexConfig = {
  name: string
  columns: readonly AnyColumn[]
  method?: string
  options?: Record<string, IndexOptionValue>
  unique?: boolean
  meta?: IndexMeta
}

export function customIndex(config: CustomIndexConfig): IndexDefinition
export function customIndex(config: LegacyCustomIndexConfig): IndexDefinition
export function customIndex(
  config: CustomIndexConfig | LegacyCustomIndexConfig,
): IndexDefinition {
  if ('columns' in config) {
    const { name, columns, method, options, unique = false, meta } = config
    return {
      columns: columns.map((c) => c.name),
      unique,
      name,
      method,
      options,
      terms: columns.map((c) => ({ type: 'column', name: c.name })),
      meta,
    }
  }

  const {
    name,
    on,
    using,
    with: withClause,
    where,
    unique = false,
    meta,
  } = config
  const hasExpression = on.some((term) => !(term instanceof Column))
  if (hasExpression && !name) {
    throw new Error('customIndex.name is required for expression indexes')
  }

  return {
    columns: on.flatMap((term) => (term instanceof Column ? [term.name] : [])),
    unique,
    name,
    method: using,
    terms: on.map((term) =>
      term instanceof Column
        ? { type: 'column', name: term.name }
        : { type: 'expression', sql: term },
    ),
    with: withClause,
    where,
    meta,
  }
}

/**
 * Creates a named index on the specified columns.
 */
export function namedIndex(
  name: string,
  ...columns: AnyColumn[]
): IndexDefinition {
  return {
    columns: columns.map((c) => c.name),
    unique: false,
    name,
    terms: columns.map((c) => ({ type: 'column', name: c.name })),
  }
}

/**
 * Creates a named unique index on the specified columns.
 */
export function namedUniqueIndex(
  name: string,
  ...columns: AnyColumn[]
): IndexDefinition {
  return {
    columns: columns.map((c) => c.name),
    unique: true,
    name,
    terms: columns.map((c) => ({ type: 'column', name: c.name })),
  }
}

/**
 * Represents a database table with typed columns.
 *
 * Provides:
 * - Type-safe column access (table.columnName)
 * - Automatically derived TypeScript types (selectType, insertType)
 * - Zod schemas for validation
 * - Insert/update helpers
 * - Dialect information for query generation
 */
export class Table<
  Columns extends Record<string, AnyColumn>,
> implements SqlFragment {
  /** Internal table name - use _name to avoid conflicts with column names */
  private readonly _tableName: string
  private readonly _columns: Columns
  private _selectSchemaCache?: ZodType<SelectType<Columns>, SelectType<Columns>>
  private _insertSchemaCache?: ZodType<InsertType<Columns>, InsertType<Columns>>
  private readonly _indexes: IndexDefinition[]
  private readonly _primaryKey: PrimaryKeyDefinition | undefined
  private readonly _triggers: PgTriggerDefinition[]
  private readonly _extensions: TableExtension[]
  private readonly _dialect: Dialect
  private readonly _withConfig: TableWithOptions
  private readonly _schema: string | undefined
  readonly _comment: string | undefined
  readonly _clusteredBy: string | undefined
  readonly _withoutRowId: boolean | undefined;

  // Type-safe column access is added dynamically
  [key: string]: unknown

  // Reserved property names that cannot be used as column names
  // Nouns use __ prefix to minimize collisions, verbs don't need it
  private static readonly RESERVED_NAMES = new Set([
    '__name',
    '__columns',
    '__indexes',
    '__primaryKey',
    '__triggers',
    '__extensions',
    '__dialect',
    '__with',
    '__sqlRef',
    '__sqlRefFor',
    '__physicalName',
    '__schema',
    '__selectType',
    '__insertType',
    '__selectSchema',
    '__insertSchema',
    '__meta__',
    '__queryContext',
    '__withoutRowId',
    'all',
    'fn',
    'delete',
    'insert',
    'unnest',
    'update',
    'make',
    'toSQL',
  ])

  // oxlint-disable-next-line typescript/no-explicit-any
  readonly _fnBuilder?: (table: any, db: QueryContext) => any

  private readonly _meta: TableMeta

  constructor(
    tableName: string,
    columns: Columns,
    dialect: Dialect = 'postgres',
    extensionBuilder?: (columns: Columns) => TableExtension[],
    indexBuilder?: (columns: Columns) => IndexDefinition[],
    primaryKeyBuilder?: (columns: Columns) => readonly AnyColumn[],
    withConfig?: TableWithConfig<Columns>,
    // oxlint-disable-next-line typescript/no-explicit-any
    fnBuilder?: (table: any, db: QueryContext) => any,
    schema?: string,
    comment?: string,
    clusteredBy?: string,
    meta?: TableMeta,
    withoutRowId?: boolean,
    triggerBuilder?: (columns: Columns) => PgTriggerDefinition[],
  ) {
    this._tableName = tableName
    this._columns = columns
    this._dialect = dialect
    this._fnBuilder = fnBuilder
    this._schema = schema
    this._comment = comment
    this._clusteredBy = clusteredBy
    this._withoutRowId = withoutRowId
    this._meta = meta ?? {}

    // Bind columns to this table and expose them as properties
    // This must happen BEFORE builders are called so column.name works
    for (const [colName, column] of Object.entries(columns)) {
      column._bind(tableName, colName, schema, dialect)

      // Only expose as property if it doesn't conflict with class properties
      if (!Table.RESERVED_NAMES.has(colName)) {
        ;(this as Record<string, unknown>)[colName] = column
      }
    }

    this._withConfig = withConfig ? withConfig(columns) : {}

    // Build extensions after columns are bound (executed before indexes in DDL)
    this._extensions = extensionBuilder ? extensionBuilder(columns) : []
    this._primaryKey = primaryKeyBuilder
      ? { columns: primaryKeyBuilder(columns).map((c) => c.name) }
      : undefined
    // Build indexes after columns are bound
    this._indexes = indexBuilder ? indexBuilder(columns) : []
    this._triggers = triggerBuilder ? triggerBuilder(columns) : []
  }

  /**
   * Gets the table name (unqualified, no schema)
   */
  get __name(): string {
    return this._tableName
  }

  /**
   * Gets the dialect-aware, schema-qualified table reference for SQL emission.
   * - Postgres/SQLite: "schema"."tableName" or "tableName"
   * - Snowflake: SCHEMA."tableName" or "tableName" (schema emitted raw/unquoted)
   */
  get __sqlRef(): string {
    return this.__sqlRefFor(this._dialect)
  }

  __sqlRefFor(dialect: Dialect): string {
    return tableSqlRef(this._tableName, this._schema, dialect)
  }

  __physicalName(dialect: Dialect): string {
    return physicalTableName(this._tableName, this._schema, dialect)
  }

  get __schema(): string | undefined {
    return this._schema
  }

  /**
   * Gets all column definitions
   */
  get __columns(): Columns {
    return this._columns
  }

  /**
   * Gets all index definitions
   */
  get __indexes(): IndexDefinition[] {
    return this._indexes
  }

  get __triggers(): PgTriggerDefinition[] {
    return this._triggers
  }

  get __primaryKey(): PrimaryKeyDefinition | undefined {
    return this._primaryKey
  }

  /**
   * Gets all post-DDL extensions
   */
  get __extensions(): TableExtension[] {
    return this._extensions
  }

  /**
   * Gets the dialect for this table (postgres or sqlite)
   */
  get __dialect(): Dialect {
    return this._dialect
  }

  /**
   * Gets table-level storage parameters (e.g., TimescaleDB compression)
   */
  get __with(): TableWithOptions {
    return this._withConfig
  }

  /**
   * Opaque table-level metadata, forwarded from the config. Consumers like
   * projection consumers can read well-known keys (e.g. `sourceTables`) off this.
   */
  get __meta__(): TableMeta {
    return this._meta
  }

  get __withoutRowId(): boolean {
    return this._withoutRowId === true
  }

  /**
   * The TypeScript type for SELECT results
   */
  get __selectType(): SelectType<Columns> {
    // This is a phantom property for type inference only
    throw new Error('__selectType is a type-only property')
  }

  /**
   * The TypeScript type for INSERT values
   */
  get __insertType(): InsertType<Columns> {
    // This is a phantom property for type inference only
    throw new Error('__insertType is a type-only property')
  }

  /**
   * Zod schema for validating SELECT results
   */
  get __selectSchema(): ZodType<SelectType<Columns>, SelectType<Columns>> {
    // Columns are fixed per instance (Column caches its zodType too): build once, not per read.
    return (this._selectSchemaCache ??= (() => {
      const shape: Record<string, ZodType> = {}

      for (const [colName, column] of Object.entries(this._columns)) {
        shape[colName] = column.zodType
      }

      return z.object(shape) as unknown as ZodType<
        SelectType<Columns>,
        SelectType<Columns>
      >
    })())
  }

  /**
   * Zod schema for validating INSERT values
   */
  get __insertSchema(): ZodType<InsertType<Columns>, InsertType<Columns>> {
    // Columns are fixed per instance (Column caches its zodType too): build once, not per read.
    return (this._insertSchemaCache ??= (() => {
      const shape: Record<string, ZodType> = {}

      for (const [colName, column] of Object.entries(this._columns)) {
        // oxlint-disable-next-line typescript-eslint/no-unsafe-assignment
        const insertionType = column.insertionType

        if (insertionType === 'generated') {
          // Generated columns are not included in insert schema
          continue
        }

        let colSchema = column.inputZodType

        if (insertionType === 'optional' || insertionType === 'withDefault') {
          colSchema = colSchema.optional()
        }

        shape[colName] = colSchema
      }

      return z.object(shape) as unknown as ZodType<
        InsertType<Columns>,
        InsertType<Columns>
      >
    })())
  }

  /**
   * Creates an InsertHelper for building INSERT statements (single or multiple rows)
   */
  insert(data: InsertType<Columns> | InsertType<Columns>[]): InsertHelper {
    const rows = Array.isArray(data) ? data : [data]
    return new InsertHelper(this, rows)
  }

  /**
   * Creates an UnnestHelper for building bulk INSERT via unnest (PostgreSQL only).
   * Uses one array parameter per column instead of one parameter per cell.
   */
  unnest(data: InsertType<Columns>[]): UnnestHelper {
    return new UnnestHelper(this, data)
  }

  /**
   * Creates an UpsertHelper for building
   * INSERT ... ON CONFLICT (conflict) DO UPDATE SET col = excluded.col.
   * On a synced db the fragment is captured as an insert or an update of the
   * existing conflicting row, so the operation replicates correctly.
   */
  upsert(
    data: InsertType<Columns>,
    options: {
      conflict: (keyof Columns & string)[]
      update: (keyof Columns & string)[]
    },
  ): UpsertHelper {
    return new UpsertHelper(this, data, options)
  }

  /**
   * Creates an UpdateHelper for building UPDATE statements
   */
  update(data: UpdateType<Columns>): UpdateHelper {
    return new UpdateHelper(this, data)
  }

  /**
   * Creates a DeleteHelper for building DELETE statements.
   */
  delete(): DeleteHelper {
    return new DeleteHelper(this)
  }

  /**
   * Creates an insert-ready object with defaults applied
   */
  make(data: InsertType<Columns>): InsertType<Columns> {
    return data
  }

  get __queryContext(): QueryContext {
    const queryContext = (
      this as Table<Columns> & { _queryContext?: QueryContext }
    )._queryContext

    if (!queryContext) {
      throw new Error(
        'Table is not bound to a database instance. Call methods via db.tableName.',
      )
    }

    return queryContext
  }

  /**
   * Returns the schema-qualified table name for use in template literals.
   */
  toString(): string {
    return this.__sqlRef
  }

  /**
   * SqlFragment implementation - outputs schema-qualified table name
   */
  toSQL(
    _paramOffset: number,
    dialect: Dialect = this._dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    return { sql: this.__sqlRefFor(dialect), params: [] }
  }

  get all(): SqlFragment {
    return allColumnsFragment(
      (dialect) => this.__sqlRefFor(dialect ?? this._dialect),
      this._columns,
    )
  }

  /**
   * Creates an aliased version of this table for use in JOINs.
   * The alias outputs "table" "alias" and columns output "alias"."column".
   */
  as(
    alias: string,
    // oxlint-disable-next-line typescript/no-explicit-any
  ): AliasedTable<Columns> & { [key: string]: AliasedColumn<any> } {
    return new AliasedTable(
      (dialect) => this.__sqlRefFor(dialect ?? this._dialect),
      alias,
      this._columns,
      // oxlint-disable-next-line typescript/no-explicit-any
    ) as AliasedTable<Columns> & { [key: string]: AliasedColumn<any> }
  }
}

/**
 * An aliased column that outputs "alias"."column" instead of "table"."column"
 */
class AliasedColumn<T> implements SqlFragment {
  constructor(
    private readonly alias: string,
    private readonly columnName: string,
    private readonly original: Column<T>,
  ) {}

  get qualifiedName(): string {
    return `"${this.alias}"."${this.columnName}"`
  }

  private isColumnRef(value: unknown): value is { qualifiedName: string } {
    return (
      value !== null &&
      typeof value === 'object' &&
      'qualifiedName' in value &&
      typeof (value as { qualifiedName: string }).qualifiedName === 'string'
    )
  }

  eq(value: T | Column<T> | AliasedColumn<T>): Condition {
    if (this.isColumnRef(value)) {
      return new Condition(`${this.qualifiedName} = ${value.qualifiedName}`, [])
    }
    const encoded = this.original.encode(value)
    return this.original.valueCondition(this.qualifiedName, '=', encoded)
  }

  ne(value: T | Column<T> | AliasedColumn<T>): Condition {
    if (this.isColumnRef(value)) {
      return new Condition(
        `${this.qualifiedName} != ${value.qualifiedName}`,
        [],
      )
    }
    const encoded = this.original.encode(value)
    return this.original.valueCondition(this.qualifiedName, '!=', encoded)
  }

  gt(value: T | Column<T> | AliasedColumn<T>): Condition {
    if (this.isColumnRef(value)) {
      return new Condition(`${this.qualifiedName} > ${value.qualifiedName}`, [])
    }
    const encoded = this.original.encode(value)
    return this.original.valueCondition(this.qualifiedName, '>', encoded)
  }

  gte(value: T | Column<T> | AliasedColumn<T>): Condition {
    if (this.isColumnRef(value)) {
      return new Condition(
        `${this.qualifiedName} >= ${value.qualifiedName}`,
        [],
      )
    }
    const encoded = this.original.encode(value)
    return this.original.valueCondition(this.qualifiedName, '>=', encoded)
  }

  lt(value: T | Column<T> | AliasedColumn<T>): Condition {
    if (this.isColumnRef(value)) {
      return new Condition(`${this.qualifiedName} < ${value.qualifiedName}`, [])
    }
    const encoded = this.original.encode(value)
    return this.original.valueCondition(this.qualifiedName, '<', encoded)
  }

  lte(value: T | Column<T> | AliasedColumn<T>): Condition {
    if (this.isColumnRef(value)) {
      return new Condition(
        `${this.qualifiedName} <= ${value.qualifiedName}`,
        [],
      )
    }
    const encoded = this.original.encode(value)
    return this.original.valueCondition(this.qualifiedName, '<=', encoded)
  }

  isNull(): Condition {
    return new Condition(`${this.qualifiedName} IS NULL`, [])
  }

  isNotNull(): Condition {
    return new Condition(`${this.qualifiedName} IS NOT NULL`, [])
  }

  like(pattern: string): Condition {
    const encoded = this.original.encode(pattern as T)
    return this.original.valueCondition(this.qualifiedName, 'LIKE', encoded)
  }

  ilike(pattern: string): Condition {
    const encoded = this.original.encode(pattern as T)
    return this.original.valueCondition(this.qualifiedName, 'ILIKE', encoded)
  }

  toSQL(_paramOffset: number): { sql: string; params: ParamWithMeta[] } {
    return { sql: this.qualifiedName, params: [] }
  }
}

// Type helper: map column types to aliased column types
type AliasedColumns<Columns extends Record<string, AnyColumn>> = {
  [K in keyof Columns]: Columns[K] extends Column<infer T, infer _I, infer _G>
    ? AliasedColumn<T>
    : never
}

/**
 * An aliased table for use in JOINs.
 * Outputs "table" "alias" and provides columns that output "alias"."column".
 */
export class AliasedTable<
  Columns extends Record<string, AnyColumn>,
> implements SqlFragment {
  private readonly _aliasedColumns: AliasedColumns<Columns>;

  [key: string]: unknown

  constructor(
    private readonly _originalRef: (dialect?: Dialect) => string,
    private readonly _alias: string,
    columns: Columns,
  ) {
    // Create aliased versions of all columns
    const aliased: Record<string, AliasedColumn<unknown>> = {}
    for (const [colName, column] of Object.entries(columns)) {
      const aliasedCol = new AliasedColumn(
        _alias,
        colName,
        column as Column<unknown>,
      )
      aliased[colName] = aliasedCol
      if (colName !== 'all') {
        ;(this as Record<string, unknown>)[colName] = aliasedCol
      }
    }
    this._aliasedColumns = aliased as AliasedColumns<Columns>
  }

  get __columns(): AliasedColumns<Columns> {
    return this._aliasedColumns
  }

  toSQL(
    _paramOffset: number,
    dialect?: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    return { sql: `${this._originalRef(dialect)} "${this._alias}"`, params: [] }
  }

  get all(): SqlFragment {
    return allColumnsFragment(`"${this._alias}"`, this._aliasedColumns)
  }
}

function allColumnsFragment(
  sourceRef: string | ((dialect?: Dialect) => string),
  columns: Record<string, SqlFragment>,
): SqlFragment {
  return {
    toSQL(
      _paramOffset: number,
      dialect?: Dialect,
    ): { sql: string; params: ParamWithMeta[] } {
      const resolvedSourceRef =
        typeof sourceRef === 'function' ? sourceRef(dialect) : sourceRef
      return {
        sql: Object.keys(columns)
          .map((columnName) => `${resolvedSourceRef}."${columnName}"`)
          .join(', '),
        params: [],
      }
    },
  }
}

/**
 * Helper class for building INSERT statements (single or multiple rows)
 */
export class InsertHelper implements SqlFragment {
  constructor(
    private readonly table: Table<Record<string, AnyColumn>>,
    private readonly rows: Record<string, unknown>[],
  ) {
    Object.defineProperty(this, immediateTransactionSql, { value: true })
  }

  toSQL(
    paramOffset: number,
    dialect?: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    const sqlDialect = dialect ?? 'postgres'
    if (this.rows.length === 0) {
      throw new Error('No rows to insert')
    }
    const columns = insertColumns(this.table, this.rows)
    if (columns.length === 0) {
      if (this.rows.length !== 1) throw new Error('Insert rows with only database defaults separately')
      return { sql: `INSERT INTO ${this.table.__sqlRefFor(dialect ?? this.table.__dialect)} DEFAULT VALUES`, params: [] }
    }

    const params: ParamWithMeta[] = []
    const valueGroups: string[] = []
    let paramIndex = paramOffset

    for (const row of this.rows) {
      const placeholders: string[] = []
      for (const colName of columns) {
        const column = this.table.__columns[colName]
        let value = row[colName]
        if (value === undefined && column.hasDefaultFn) {
          value = column.applyDefaultFn()
        }
        if (value === undefined && column.metadata.defaultValue !== undefined) {
          if (sqlDialect === 'sqlite') throw new Error(`Insert rows with different omitted database defaults separately: ${colName}`)
          placeholders.push('DEFAULT')
          continue
        }
        if (value === undefined && column.metadata.isNullable) {
          value = null
        }
        const encoded = column.encode(value)
        const rendered = column.valueSql(encoded, paramIndex, sqlDialect)
        params.push(...rendered.params)
        paramIndex += rendered.params.length
        placeholders.push(rendered.sql)
      }
      valueGroups.push(`(${placeholders.join(', ')})`)
    }

    const quotedColumns = columns.map((c) => `"${c}"`).join(', ')
    const sql = `INSERT INTO ${this.table.__sqlRefFor(dialect ?? this.table.__dialect)} (${quotedColumns}) VALUES ${valueGroups.join(', ')}`
    return { sql, params }
  }
}

export class UpsertHelper implements SqlFragment {
  constructor(
    private readonly table: Table<Record<string, AnyColumn>>,
    private readonly row: Record<string, unknown>,
    private readonly options: { conflict: string[]; update: string[] },
  ) {
    Object.defineProperty(this, immediateTransactionSql, { value: true })
  }

  toSQL(
    paramOffset: number,
    dialect?: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    const sqlDialect = dialect ?? 'postgres'
    if (sqlDialect === 'snowflake') {
      throw new Error('upsert is not supported for the snowflake dialect')
    }
    const { conflict, update } = this.options
    if (conflict.length === 0) {
      throw new Error('upsert requires at least one conflict column')
    }
    if (update.length === 0) {
      throw new Error('upsert requires at least one update column')
    }
    for (const colName of [...conflict, ...update]) {
      if (!this.table.__columns[colName]) {
        throw new Error(`Unknown column: ${colName}`)
      }
    }
    for (const colName of update) {
      assertWritableColumn(colName, this.table.__columns[colName])
      if (!Object.hasOwn(this.row, colName) || this.row[colName] === undefined) {
        throw new Error(`Upsert update requires an explicit value: ${colName}`)
      }
    }

    const insert = new InsertHelper(this.table, [this.row]).toSQL(
      paramOffset,
      dialect,
    )
    const conflictColumns = conflict.map((c) => `"${c}"`).join(', ')
    const assignments = update
      .map((c) => `"${c}" = excluded."${c}"`)
      .join(', ')
    const sql = `${insert.sql} ON CONFLICT (${conflictColumns}) DO UPDATE SET ${assignments}`
    return { sql, params: insert.params }
  }
}

/**
 * Maps a column's _dataType to the PostgreSQL array cast suffix.
 * e.g. "VARCHAR(100)" -> "varchar[]", "TIMESTAMPTZ" -> "timestamptz[]"
 */
function pgArrayCast(dataType: string): string {
  const dt = dataType.toUpperCase()
  // Enum columns have quoted type names like "status_enum"
  if (dataType.startsWith('"')) return `${dataType}[]`
  // Strip precision/length for most types
  if (dt.startsWith('VARCHAR')) return 'varchar[]'
  if (dt.startsWith('NUMERIC')) return 'numeric[]'
  if (dt.startsWith('CHAR')) return `${dataType.toLowerCase()}[]`
  if (dt === 'SERIAL') return 'integer[]'
  return `${dataType.toLowerCase()}[]`
}

/**
 * Helper class for building INSERT via unnest (PostgreSQL bulk insert).
 *
 * Instead of `VALUES ($1,$2),($3,$4)` (one param per cell),
 * emits `SELECT ... FROM unnest($1::type[], $2::type[]) AS _u(...)` (one array param per column).
 * This avoids the 65535 parameter limit and is faster for large batches.
 */
export class UnnestHelper implements SqlFragment {
  constructor(
    private readonly table: Table<Record<string, AnyColumn>>,
    private readonly rows: Record<string, unknown>[],
  ) {}

  toSQL(
    paramOffset: number,
    dialect?: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    if (this.rows.length === 0) {
      throw new Error('No rows to unnest')
    }
    const columns = insertColumns(this.table, this.rows)

    if (columns.length === 0 || columns.some(name => {
      const column = this.table.__columns[name]
      return column.metadata.defaultValue !== undefined && !column.hasDefaultFn
        && this.rows.some(row => row[name] === undefined)
    })) {
      return new InsertHelper(this.table, this.rows).toSQL(paramOffset, dialect)
    }

    const preparedColumns = columns.map((name) => {
      const column = this.table.__columns[name]
      return { name, column, isNullable: column.metadata.isNullable }
    })
    const columnArrays: unknown[][] = columns.map(
      // oxlint-disable-next-line unicorn/no-new-array -- Known row count; Array.from adds a measured initialization pass.
      () => new Array<unknown>(this.rows.length),
    )
    for (let r = 0; r < this.rows.length; r++) {
      const row = this.rows[r]
      for (let c = 0; c < preparedColumns.length; c++) {
        const { name, column, isNullable } = preparedColumns[c]
        let value = row[name]
        if (value === undefined && column.hasDefaultFn) {
          value = column.applyDefaultFn()
        }
        if (value === undefined && isNullable) {
          value = null
        }
        const encoded = column.encode(value)
        columnArrays[c][r] = encoded
      }
    }

    // Build params and unnest args
    const params: ParamWithMeta[] = []
    const unnestArgs: string[] = []
    let paramIndex = paramOffset

    for (let c = 0; c < columns.length; c++) {
      const colName = columns[c]
      const column = this.table.__columns[colName]
      const arrayCast = pgArrayCast(column.metadata.dataType)
      unnestArgs.push(`$${paramIndex++}::${arrayCast}`)
      // Enum arrays must be serialized as PG array literals so drivers
      // that don't know the custom type OID (e.g. PGlite) can handle them.
      const isEnum = column.metadata.dataType.startsWith('"')
      const value = isEnum
        ? `{${columnArrays[c].map((v) => `${v}`).join(',')}}`
        : columnArrays[c]
      params.push({ value, columnName: colName })
    }

    const quotedColumns = columns.map((c) => `"${c}"`).join(', ')
    const sql = [
      `INSERT INTO ${this.table.__sqlRefFor(dialect ?? this.table.__dialect)} (${quotedColumns})`,
      `SELECT ${quotedColumns} FROM unnest(${unnestArgs.join(', ')})`,
      `AS _u(${quotedColumns})`,
    ].join(' ')

    return { sql, params }
  }
}

/**
 * Helper class for building UPDATE statements
 */
export class UpdateHelper implements SqlFragment {
  constructor(
    private readonly table: Table<Record<string, AnyColumn>>,
    private readonly data: Record<string, unknown>,
  ) {
    Object.defineProperty(this, immediateTransactionSql, { value: true })
  }

  toSQL(
    paramOffset: number,
    dialect?: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    const sqlDialect = dialect ?? 'postgres'
    const setParts: string[] = []
    const params: ParamWithMeta[] = []
    let paramIndex = paramOffset

    for (const [colName, value] of Object.entries(this.data)) {
      if (value === undefined) {
        // Sparse update: skip undefined fields
        continue
      }

      const column = this.table.__columns[colName]
      if (!column) {
        throw new Error(`Unknown column: ${colName}`)
      }
      assertWritableColumn(colName, column)

      if (isSqlFragment(value)) {
        const rendered = value.toSQL(paramIndex, sqlDialect)
        paramIndex += rendered.params.length
        setParts.push(`"${colName}" = ${rendered.sql}`)
        params.push(...rendered.params)
        continue
      }

      const encoded = column.encode(value)
      const rendered = column.valueSql(encoded, paramIndex, sqlDialect)
      paramIndex += rendered.params.length
      setParts.push(`"${colName}" = ${rendered.sql}`)
      params.push(...rendered.params)
    }

    if (setParts.length === 0) {
      throw new Error('No fields to update')
    }

    const sql = `UPDATE ${this.table.__sqlRefFor(dialect ?? this.table.__dialect)} SET ${setParts.join(', ')}`
    return { sql, params }
  }
}

function isSqlFragment(value: unknown): value is SqlFragment {
  return (
    value !== null &&
    typeof value === 'object' &&
    'toSQL' in value &&
    typeof (value as SqlFragment).toSQL === 'function'
  )
}

/**
 * Helper class for building DELETE statements.
 */
export class DeleteHelper implements SqlFragment {
  constructor(private readonly table: Table<Record<string, AnyColumn>>) {
    Object.defineProperty(this, immediateTransactionSql, { value: true })
  }

  toSQL(
    _paramOffset: number,
    dialect?: Dialect,
  ): { sql: string; params: ParamWithMeta[] } {
    return {
      sql: `DELETE FROM ${this.table.__sqlRefFor(dialect ?? this.table.__dialect)}`,
      params: [],
    }
  }
}

function assertWritableColumn(colName: string, column: AnyColumn): void {
  if (column.insertionType === 'generated') {
    throw new Error(`Generated column cannot be written: ${colName}`)
  }
}

function insertColumns(
  table: Table<Record<string, AnyColumn>>,
  rows: Record<string, unknown>[],
): string[] {
  const columns = new Set<string>()
  for (const row of rows) {
    for (const [name, value] of Object.entries(row)) {
      if (value === undefined) continue
      const column = table.__columns[name]
      if (!column) throw new Error(`Unknown column: ${name}`)
      assertWritableColumn(name, column)
      columns.add(name)
    }
  }
  for (const [name, column] of Object.entries(table.__columns)) {
    if (column.hasDefaultFn) columns.add(name)
  }
  return [...columns]
}

// Re-export config types that dialect-specific table factories need
export type { TableConfig, SqliteTableConfig }
